import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ, OM Selected" },
      { name: "description", content: "Antworten für Fotograf:innen und Visagist:innen zur Bewerbung, zum Selected-Bereich und für Kund:innen, die Kreative suchen." },
      { property: "og:title", content: "FAQ, OM Selected" },
      { property: "og:description", content: "Häufige Fragen zu Bewerbung, Selected und Anfragen bei OM Selected." },
      { property: "og:url", content: "https://omselected.art/faq" },
    ],
    links: [{ rel: "canonical", href: "https://omselected.art/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [...creatorFaq, ...selectedFaq, ...clientFaq].map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  }),
  component: FaqPage,
});

type QA = { q: string; a: string };

const creatorFaq: QA[] = [
  {
    q: "Was bringt mir eine Bewerbung bei OM Selected?",
    a: "Du wirst Teil einer kuratierten Redaktion, kein offener Marktplatz, kein Bewertungssystem. Wir empfehlen dich direkt an Kund:innen aus DACH, die nach Handschrift und nicht nach Sternen suchen. Dein Profil bleibt schlank: wenige Bilder, ein Statement, eine Preisrange.",
  },
  {
    q: "Was kostet die Aufnahme?",
    a: "Die Bewerbung ist kostenlos. Wenn du aufgenommen wirst, ist die Mitgliedschaft im Magazin in der Startphase gratis. Wir kommunizieren rechtzeitig, falls sich daran etwas ändert.",
  },
  {
    q: "Wer wird aufgenommen?",
    a: "Wir kuratieren nach Handschrift, Konsistenz und Haltung, bei Fotograf:innen wie bei Visagist:innen. Es geht nicht um Reichweite, Awards oder Ausrüstung, sondern um Arbeiten, die wir selbst gerne an die Wand hängen würden.",
  },
  {
    q: "Wie läuft die Bewerbung ab?",
    a: "Du füllst das Bewerbungsformular aus, schickst Beispielbilder und einen kurzen Text. Unsere Redaktion sichtet jede Einreichung persönlich und meldet sich innerhalb weniger Tage.",
  },
  {
    q: "Bekomme ich Anfragen direkt?",
    a: "Ja. Wenn Kund:innen über uns ein Brief schicken, schlagen wir zwei bis drei passende Fotograf:innen oder Visagist:innen vor. Der Kontakt läuft danach direkt zwischen dir und den Kund:innen, wir nehmen keine Provision.",
  },
  {
    q: "Kann ich mein Profil später bearbeiten?",
    a: "Ja. Nach der Aufnahme bekommst du einen Login. Im Bereich „Mein Profil“ kannst du Galerie, Themen, Bio und Preise selbst pflegen.",
  },
];

const selectedFaq: QA[] = [
  {
    q: "Was ist der Selected-Bereich?",
    a: "Selected ist unsere wöchentliche Auswahl an Bildern, eine Art Magazinstrecke. Fotograf:innen und Visagist:innen können Arbeiten einreichen, wir wählen kuratiert aus.",
  },
  {
    q: "Werden alle eingereichten Bilder veröffentlicht?",
    a: "Nein. Wir wählen bewusst nur wenige Bilder pro Woche aus. Eine Einreichung ist keine Garantie für eine Veröffentlichung. Wenn ein Bild nicht passt, hat das nichts mit der Qualität deiner Arbeit insgesamt zu tun, wir kuratieren nach Wochenthema, Bildsprache und Stimmung.",
  },
  {
    q: "Wie oft kann ich einreichen?",
    a: "So oft du möchtest. Pro Einreichung sind aktuell eine begrenzte Anzahl Bilder erlaubt, das aktuelle Limit siehst du im Einreichungsformular.",
  },
  {
    q: "Was passiert, wenn ein Bild ausgewählt wird?",
    a: "Wir veröffentlichen es im Selected-Bereich, verlinken dein Profil und stellen die Arbeit als wöchentliches Highlight heraus. Du behältst alle Rechte am Bild.",
  },
  {
    q: "Welche Bilder eignen sich besonders?",
    a: "Ehrliche, unaufgeregte Arbeiten mit klarer Handschrift. Wir bevorzugen Serien-Denken statt einzelner Hero-Shots und Bilder, die einen Moment statt einer Pose zeigen.",
  },
];

const clientFaq: QA[] = [
  {
    q: "Wie funktioniert OM Selected für Kunden?",
    a: "Du schickst uns ein kurzes Brief, Anlass, Region, Budget, Datumsfenster. Innerhalb von 48 Stunden schlagen wir dir zwei bis drei passende Fotograf:innen oder Visagist:innen aus unserer kuratierten Auswahl vor.",
  },
  {
    q: "Was kostet der Service?",
    a: "Für dich nichts. Wir nehmen keine Provision und versteckte Gebühren gibt es nicht. Du buchst die Kreativen direkt zu ihren Konditionen.",
  },
  {
    q: "Wie schnell bekomme ich eine Antwort?",
    a: "In der Regel innerhalb von 48 Stunden, oft schneller. Bei kurzfristigen Anfragen schreib uns gerne dazu, wie eilig es ist.",
  },
  {
    q: "Was, wenn keine:r der Vorgeschlagenen passt?",
    a: "Sag uns kurz, was nicht stimmt, Stil, Verfügbarkeit, Budget, und wir suchen weiter. Das Brief ist unverbindlich.",
  },
  {
    q: "Wie wird die Auswahl getroffen?",
    a: "Wir kennen jede:n Kreative:n im Magazin persönlich. Auf Basis deines Briefs vergleichen wir Handschrift, Region, Themen-Erfahrung und Preisrange, kein Algorithmus, sondern eine redaktionelle Empfehlung.",
  },
  {
    q: "Wem gehören die Bilder?",
    a: "Du klärst Nutzungsrechte direkt mit den Fotograf:innen, das ist je nach Anlass (privat, kommerziell, redaktionell) unterschiedlich. Wir helfen gerne, falls Fragen auftauchen.",
  },
];

function Section({ title, eyebrow, items }: { title: string; eyebrow: string; items: QA[] }) {
  return (
    <section className="border-b border-border">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="eyebrow text-muted-foreground">{eyebrow}</div>
          <h2 className="serif text-3xl md:text-4xl mt-3 leading-tight">{title}</h2>
        </div>
        <div className="md:col-span-8 divide-y divide-border">
          {items.map((item) => (
            <details key={item.q} className="group py-5">
              <summary className="flex justify-between items-start gap-6 cursor-pointer list-none">
                <h3 className="serif text-xl leading-snug">{item.q}</h3>
                <span className="eyebrow text-muted-foreground shrink-0 mt-1 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqPage() {
  const { t } = useTranslation();
  const creator = t("faq.creatorItems", { returnObjects: true }) as QA[];
  const selected = t("faq.selectedItems", { returnObjects: true }) as QA[];
  const client = t("faq.clientItems", { returnObjects: true }) as QA[];
  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-12 pb-16">
          <div className="eyebrow text-muted-foreground">{t("faq.eyebrow")}</div>
          <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">{t("faq.title")}</h1>
          <p className="mt-6 max-w-xl text-muted-foreground">
            {t("faq.intro")}
          </p>
        </div>
      </section>

      <Section eyebrow={t("faq.creatorEyebrow")} title={t("faq.creatorTitle")} items={creator} />
      <Section eyebrow={t("faq.selectedEyebrow")} title={t("faq.selectedTitle")} items={selected} />
      <Section eyebrow={t("faq.clientEyebrow")} title={t("faq.clientTitle")} items={client} />

      <section>
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-20 text-center">
          <p className="serif text-2xl md:text-3xl italic leading-snug max-w-2xl mx-auto">
            {t("faq.ctaText")}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3 eyebrow">
            <Link to="/apply" className="border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors">{t("faq.ctaApply")}</Link>
            <Link to="/brief" className="border border-border px-5 py-3 hover:border-foreground transition-colors">{t("faq.ctaBrief")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
